import type { CustomQuestion, KnownQuestion } from "./CallScriptManager";

export function normalizeProcedureCode(code: string): string {
  return code.trim().toUpperCase();
}

function applyProcedureCodeToTemplate(
  templateText: string,
  templatePath: string | null,
  procedureCode: string,
): Pick<CustomQuestion, "question_text" | "results_field_path" | "procedure_code"> {
  const code = normalizeProcedureCode(procedureCode);
  return {
    procedure_code: code,
    results_field_path: templatePath?.replace(/XYZ/g, code) ?? null,
    question_text: templateText.replace(/XYZ/g, code),
  };
}

export function customizeKnownQuestionWithProcedureCode(
  known: KnownQuestion,
  procedureCode: string,
): CustomQuestion {
  const templateText = known.question_text;
  const templatePath = known.results_field_path;
  return {
    ...known,
    id: crypto.randomUUID(),
    ...applyProcedureCodeToTemplate(templateText, templatePath, procedureCode),
    procedure_code_source_text: templateText,
    procedure_code_source_path: templatePath,
    is_custom: true,
  };
}

export function updateCustomQuestionProcedureCode(
  question: CustomQuestion,
  newCode: string,
): CustomQuestion {
  const templateText = question.procedure_code_source_text;
  const templatePath = question.procedure_code_source_path ?? null;

  if (templateText) {
    return {
      ...question,
      ...applyProcedureCodeToTemplate(templateText, templatePath, newCode),
    };
  }

  const normalizedNew = normalizeProcedureCode(newCode);
  const normalizedOld = normalizeProcedureCode(question.procedure_code ?? "");
  if (normalizedOld === normalizedNew) {
    return question;
  }

  // Legacy custom questions without stored template text — only replace when old code is non-empty.
  if (!normalizedOld) {
    return {
      ...question,
      procedure_code: normalizedNew,
      question_text: question.question_text.replace(/XYZ/g, normalizedNew),
      results_field_path:
        question.results_field_path?.replace(/XYZ/g, normalizedNew) ?? null,
    };
  }

  const escapedOld = normalizedOld.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return {
    ...question,
    procedure_code: normalizedNew,
    results_field_path:
      question.results_field_path
        ?.replace(`procedure_codes.${normalizedOld}`, `procedure_codes.${normalizedNew}`)
        .replace(/XYZ/g, normalizedNew) ?? null,
    question_text: question.question_text
      .replace(new RegExp(escapedOld, "g"), normalizedNew)
      .replace(/XYZ/g, normalizedNew),
  };
}
